import { useEffect, useState, forwardRef, useImperativeHandle, type MouseEvent } from 'react';
import { ChevronRight, EyeOff, Plus, Trash2, Users, XCircle } from 'lucide-react';
import { db } from '@/lib/db';
import type { GroupRoundListItem, Member } from '@/lib/db/types';
import {
  getPlayTabCache,
  loadPlayTabData,
  removeRoundFromPlayTabCache,
} from '@/lib/play-tab-data';
import { CreateRoundWizard } from './CreateRoundWizard';
import { ActiveRoundSheet } from './ActiveRoundSheet';
import { RoundResultsSheet } from './RoundResultsSheet';
import { PlayTabHomeSkeleton } from './RoundLoadingSkeleton';
import { AlertDialog } from '@/components/shared/AlertDialog';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';

export interface CreateRoundTabHandle {
  openStartRound: () => void;
}

interface Props {
  member: Member;
}

type Mode = 'home' | 'create' | 'active' | 'results';

function applyCache(
  data: NonNullable<ReturnType<typeof getPlayTabCache>>,
  setters: {
    setActiveRounds: (v: GroupRoundListItem[]) => void;
    setHistory: (v: GroupRoundListItem[]) => void;
    setJoinableRounds: (v: GroupRoundListItem[]) => void;
    setCourseNames: (v: Record<string, string>) => void;
  },
) {
  setters.setActiveRounds(data.activeRounds);
  setters.setHistory(data.history);
  setters.setJoinableRounds(data.joinableRounds);
  setters.setCourseNames(data.courseNames);
}

export const CreateRoundTab = forwardRef<CreateRoundTabHandle, Props>(function CreateRoundTab({ member }, ref) {
  const initial = getPlayTabCache();
  const [mode, setMode] = useState<Mode>('home');
  const [activeRounds, setActiveRounds] = useState<GroupRoundListItem[]>(() => initial?.activeRounds ?? []);
  const [joinableRounds, setJoinableRounds] = useState<GroupRoundListItem[]>(() => initial?.joinableRounds ?? []);
  const [courseNames, setCourseNames] = useState<Record<string, string>>(() => initial?.courseNames ?? {});
  const [history, setHistory] = useState<GroupRoundListItem[]>(() => initial?.history ?? []);
  const [roundId, setRoundId] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(!initial);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [requestedIds, setRequestedIds] = useState<Set<string>>(new Set());
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [hidingId, setHidingId] = useState<string | null>(null);
  const [pendingHideId, setPendingHideId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [pendingCancelId, setPendingCancelId] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertTitle, setAlertTitle] = useState('Something went wrong');

  const hasListData = activeRounds.length > 0 || history.length > 0 || joinableRounds.length > 0;
  const formatRoundDateTime = (iso: string) =>
    new Date(iso).toLocaleString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

  const roundHostFirstName = (round: GroupRoundListItem) => {
    if (round.organizer_id === member.id) return 'You';
    const host = round.players?.find((p) => p.is_organizer || p.member_id === round.organizer_id);
    return host?.display_name.split(' ')[0] ?? 'Member';
  };

  const roundPlayerNames = (round: GroupRoundListItem) =>
    (round.players || []).map((p) => p.display_name.split(' ')[0]).join(', ');

  const refresh = async (opts: { force?: boolean; background?: boolean } = {}) => {
    if (!opts.background && !hasListData && !getPlayTabCache()) {
      setInitialLoad(true);
    }
    try {
      const data = await loadPlayTabData({
        force: opts.force,
        onStage: opts.background
          ? undefined
          : (partial) => {
              setActiveRounds(partial.activeRounds);
              setCourseNames(partial.courseNames);
              if (partial.activeRounds.length > 0 || partial.courseNames && Object.keys(partial.courseNames).length > 0) {
                setInitialLoad(false);
              }
            },
      });
      applyCache(data, {
        setActiveRounds,
        setHistory,
        setJoinableRounds,
        setCourseNames,
      });
      if (data.activeRounds.length === 1 && mode === 'home') {
        setRoundId(data.activeRounds[0].id);
      }
    } finally {
      setInitialLoad(false);
    }
  };

  const loadRequested = async () => {
    try {
      const ids = await db.groupRounds.listMyPendingRequestRoundIds();
      setRequestedIds(new Set(ids));
    } catch {
      // non-critical: leave request state as-is
    }
  };

  useEffect(() => {
    void refresh({ background: !!initial });
    void loadRequested();
  }, []);

  useImperativeHandle(ref, () => ({
    openStartRound: () => {
      setMode('create');
    },
  }));

  const openCreate = () => {
    setMode('create');
  };

  const handleRequestJoin = async (id: string) => {
    setJoiningId(id);
    try {
      await db.groupRounds.requestJoin(id);
      setRequestedIds((prev) => new Set(prev).add(id));
      setAlertTitle('Request sent');
      setAlertMessage('The organizer will get a notification to add you to the round.');
    } catch (err) {
      setAlertTitle('Something went wrong');
      setAlertMessage((err as Error).message || 'Could not send request');
    } finally {
      setJoiningId(null);
    }
  };

  const requestDeleteRound = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    setPendingDeleteId(id);
  };

  const requestHideRound = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    setPendingHideId(id);
  };

  const requestCancelRound = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    setPendingCancelId(id);
  };

  const confirmDeleteRound = async () => {
    const id = pendingDeleteId;
    if (!id) return;

    const prevActive = activeRounds;
    const prevHistory = history;
    const prevJoinable = joinableRounds;

    setActiveRounds((prev) => prev.filter((r) => r.id !== id));
    setHistory((prev) => prev.filter((r) => r.id !== id));
    setJoinableRounds((prev) => prev.filter((r) => r.id !== id));
    removeRoundFromPlayTabCache(id);
    setPendingDeleteId(null);

    if (roundId === id) {
      setRoundId(null);
      setMode('home');
    }

    setDeletingId(id);
    try {
      await db.groupRounds.delete(id);
    } catch (err) {
      setActiveRounds(prevActive);
      setHistory(prevHistory);
      setJoinableRounds(prevJoinable);
      setAlertMessage((err as Error).message || 'Could not delete round');
      void refresh({ force: true, background: true });
    } finally {
      setDeletingId(null);
    }
  };

  const confirmHideRound = async () => {
    const id = pendingHideId;
    if (!id) return;
    const prevActive = activeRounds;
    const prevHistory = history;
    const prevJoinable = joinableRounds;
    setActiveRounds((prev) => prev.filter((r) => r.id !== id));
    setHistory((prev) => prev.filter((r) => r.id !== id));
    setJoinableRounds((prev) => prev.filter((r) => r.id !== id));
    removeRoundFromPlayTabCache(id);
    setPendingHideId(null);

    if (roundId === id) {
      setRoundId(null);
      setMode('home');
    }

    setHidingId(id);
    try {
      await db.groupRounds.hideFromMyHistory(id);
    } catch (err) {
      setActiveRounds(prevActive);
      setHistory(prevHistory);
      setJoinableRounds(prevJoinable);
      setAlertMessage((err as Error).message || 'Could not hide round');
      void refresh({ force: true, background: true });
    } finally {
      setHidingId(null);
    }
  };

  const confirmCancelRound = async () => {
    const id = pendingCancelId;
    if (!id) return;

    const prevActive = activeRounds;
    const prevJoinable = joinableRounds;

    setActiveRounds((prev) => prev.filter((r) => r.id !== id));
    setJoinableRounds((prev) => prev.filter((r) => r.id !== id));
    removeRoundFromPlayTabCache(id);
    setPendingCancelId(null);

    if (roundId === id) {
      setRoundId(null);
      setMode('home');
    }

    setCancellingId(id);
    try {
      await db.groupRounds.cancel(id);
    } catch (err) {
      setActiveRounds(prevActive);
      setJoinableRounds(prevJoinable);
      setAlertMessage((err as Error).message || 'Could not cancel round');
      void refresh({ force: true, background: true });
    } finally {
      setCancellingId(null);
    }
  };

  if (mode === 'create') {
    return (
      <CreateRoundWizard
        member={member}
        onClose={() => { setMode('home'); void refresh({ force: true, background: true }); }}
        onStarted={(id) => {
          setRoundId(id);
          setMode('active');
          void refresh({ force: true, background: true });
        }}
      />
    );
  }

  if (mode === 'active' && roundId) {
    return (
      <ActiveRoundSheet
        member={member}
        roundId={roundId}
        onClose={() => { setMode('home'); setRoundId(null); void refresh({ force: true, background: true }); }}
        onComplete={(id) => {
          setRoundId(id);
          setMode('results');
          void refresh({ force: true, background: true });
        }}
      />
    );
  }

  if (mode === 'results' && roundId) {
    return (
      <RoundResultsSheet
        member={member}
        roundId={roundId}
        onClose={() => { setMode('home'); setRoundId(null); void refresh({ force: true, background: true }); }}
        onPosted={() => void refresh({ force: true, background: true })}
      />
    );
  }

  return (
    <div className="space-y-4 px-4 pb-8 pt-2">
      <div>
        <div className="text-[10px] font-extrabold tracking-[0.15em] text-gold-deep">PLAY</div>
        <h1 className="font-serif text-[22px] font-bold tracking-tight text-ink">Start a Round</h1>
        <p className="mt-1 text-[13px] leading-relaxed text-ink-soft">
          Course and players first — add games when you&apos;re ready, or skip and just keep score.
        </p>
      </div>

      <button
        type="button"
        onClick={() => openCreate()}
        className="flex w-full items-center gap-3 rounded-xl border-2 border-navy bg-navy p-4 text-left shadow-bt"
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold text-xl text-navy">+</div>
        <div className="min-w-0 flex-1">
          <div className="font-serif text-base font-bold text-cream">Create Round</div>
          <div className="mt-0.5 text-xs text-cream/75">Course · players · start</div>
        </div>
        <ChevronRight size={18} className="text-cream/70" />
      </button>

      <div className="min-h-[200px]">
        {initialLoad && !hasListData ? (
          <PlayTabHomeSkeleton />
        ) : (
          <>
            {joinableRounds.length > 0 && (
              <div className="mb-4">
                <h2 className="mb-2 flex items-center gap-1.5 font-serif text-lg font-bold text-ink">
                  <Users size={18} className="text-gold-deep" />
                  Open Groups
                </h2>
                {joinableRounds.map((r) => (
                  <div
                    key={r.id}
                    className="mb-2 flex items-center justify-between rounded-[14px] border border-gold/30 bg-gold/5 p-3.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-ink">
                        {courseNames[r.course_id] || 'Course TBD'}
                      </div>
                      <div className="text-xs text-mute">
                        Hosted by {roundHostFirstName(r)}
                        {' · '}
                        {(r.players?.length ?? 0)}/4 players
                        {(r.players?.length ?? 0) > 0
                          ? ` · ${roundPlayerNames(r)}`
                          : ''}
                        {' · '}
                        {formatRoundDateTime(r.played_at)}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={joiningId === r.id || requestedIds.has(r.id)}
                      onClick={() => void handleRequestJoin(r.id)}
                      className="shrink-0 rounded-lg bg-navy px-3 py-2 text-xs font-bold text-cream disabled:opacity-60"
                    >
                      {joiningId === r.id
                        ? 'Sending…'
                        : requestedIds.has(r.id)
                          ? 'Requested'
                          : 'Request to join'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {activeRounds.length > 0 && (
              <div className="mb-4">
                <h2 className="mb-2 font-serif text-lg font-bold text-ink">In Progress</h2>
                {activeRounds.map((r) => {
                  const isOrganizer = r.organizer_id === member.id;
                  const playerNames = roundPlayerNames(r);
                  return (
                    <div key={r.id} className="mb-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => { setRoundId(r.id); setMode('active'); }}
                        className="flex min-w-0 flex-1 items-center justify-between rounded-[14px] bg-paper p-3.5 text-left shadow-bt"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-bold text-ink">
                            {courseNames[r.course_id] || 'Resume round'}
                          </div>
                          <div className="mt-0.5 truncate text-xs text-mute">
                            {isOrganizer ? 'You created' : `Hosted by ${roundHostFirstName(r)}`}
                            {' · '}
                            {(r.players?.length ?? 0)} player{(r.players?.length ?? 0) === 1 ? '' : 's'}
                            {playerNames ? ` · ${playerNames}` : ''}
                          </div>
                        </div>
                        <ChevronRight size={16} className="shrink-0 text-mute" />
                      </button>
                      {isOrganizer ? (
                        <>
                          <button
                            type="button"
                            disabled={cancellingId === r.id}
                            onClick={(e) => requestCancelRound(r.id, e)}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-line bg-paper text-ink-soft disabled:opacity-50"
                            aria-label="Cancel round"
                          >
                            <XCircle size={16} />
                          </button>
                          <button
                            type="button"
                            disabled={deletingId === r.id}
                            onClick={(e) => requestDeleteRound(r.id, e)}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-bad/30 bg-bad/5 text-bad disabled:opacity-50"
                            aria-label="Delete round"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={hidingId === r.id}
                          onClick={(e) => requestHideRound(r.id, e)}
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-line bg-paper text-mute disabled:opacity-50"
                          aria-label="Hide round from my history"
                        >
                          <EyeOff size={16} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {history.length > 0 && (
              <div>
                <h2 className="mb-2 font-serif text-lg font-bold text-ink">Recent Rounds</h2>
                {history.slice(0, 5).map((r) => {
                  const playerNames = (r.players || []).map((p) => p.display_name.split(' ')[0]).join(', ');
                  const isOrganizer = r.organizer_id === member.id;
                  return (
                    <div key={r.id} className="mb-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => { setRoundId(r.id); setMode('results'); }}
                        className="flex min-w-0 flex-1 items-center justify-between rounded-[14px] border border-line bg-paper p-3.5 text-left shadow-bt"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-bold text-ink">
                            {courseNames[r.course_id] || 'Round'}
                          </div>
                          <div className="mt-0.5 truncate text-xs text-mute">
                            {formatRoundDateTime(r.played_at)}
                            {playerNames ? ` · ${playerNames}` : ''}
                          </div>
                        </div>
                        <ChevronRight size={16} className="shrink-0 text-mute" />
                      </button>
                      {isOrganizer && (
                        <button
                          type="button"
                          disabled={deletingId === r.id}
                          onClick={(e) => requestDeleteRound(r.id, e)}
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-bad/30 bg-bad/5 text-bad disabled:opacity-50"
                          aria-label="Delete round"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!initialLoad && !hasListData && (
              <div className="rounded-xl border border-dashed border-line py-10 text-center">
                <Plus className="mx-auto mb-2 text-mute" size={24} />
                <p className="text-sm text-mute">No rounds yet. Tap Create Round to get started.</p>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={pendingDeleteId != null}
        title="Delete this round?"
        message="This cannot be undone."
        confirmLabel="Delete"
        destructive
        busy={deletingId === pendingDeleteId}
        onConfirm={() => void confirmDeleteRound()}
        onCancel={() => setPendingDeleteId(null)}
      />
      <ConfirmDialog
        open={pendingHideId != null}
        title="Remove from Play tab?"
        message="This removes the round from your Play tab. Other players are not affected."
        confirmLabel="Remove"
        busy={hidingId === pendingHideId}
        onConfirm={() => void confirmHideRound()}
        onCancel={() => setPendingHideId(null)}
      />
      <ConfirmDialog
        open={pendingCancelId != null}
        title="Cancel this round?"
        message="The round will end for everyone. Scores are not posted to the feed."
        confirmLabel="Cancel round"
        destructive
        busy={cancellingId === pendingCancelId}
        onConfirm={() => void confirmCancelRound()}
        onCancel={() => setPendingCancelId(null)}
      />
      <AlertDialog
        open={alertMessage != null}
        title={alertTitle}
        message={alertMessage}
        onClose={() => setAlertMessage(null)}
      />
    </div>
  );
});
